
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, X, FileText, Printer, ChevronDown, Pencil, XCircle } from 'lucide-react';
import ConfirmationModal from '../common/ConfirmationModal';

const QuoteMessage = ({ message, user, onRespond, onEdit, chatInfo }) => {
    // PROTEÇÃO: Se 'quote' não existir (dados antigos), usa a própria 'message' como fallback
    const quoteData = message.quote || message;
    const { senderId, proposalId } = message;

    const [isExpanded, setIsExpanded] = useState(false);
    const [confirmConfig, setConfirmConfig] = useState(null);
    const navigate = useNavigate();

    const isMyQuote = user.uid === senderId;
    const isClient = !isMyQuote;

    const statusDisplay = {
        pending: { text: 'Aguardando Resposta', className: 'bg-yellow-400 text-yellow-900' },
        accepted: { text: 'Proposta Aceita', className: 'bg-green-500 text-white' },
        rejected: { text: 'Proposta Recusada', className: 'bg-red-500 text-white' },
        canceled: { text: 'Proposta Cancelada', className: 'bg-gray-500 text-white' },
    };

    const currentStatus = statusDisplay[quoteData.status] || { text: quoteData.status || 'Pendente', className: 'bg-gray-400 text-white' };

    const handlePrint = (e) => { e.stopPropagation(); navigate('/print-quote', { state: { message, chatInfo } }); };

    const requestConfirmation = (status) => {
        let config = {
            status,
            onConfirm: () => {
                onRespond(proposalId, status);
                setConfirmConfig(null);
            }
        };

        if (status === 'accepted') {
            config.title = "Aceitar Orçamento";
            config.message = "Você deseja aceitar esta proposta? O prestador será notificado para iniciar o serviço.";
            config.confirmText = "Sim, Aceitar";
        } else if (status === 'rejected') {
            config.title = "Recusar Orçamento";
            config.message = "Tem certeza que deseja recusar esta proposta?";
            config.confirmText = "Sim, Recusar";
        } else if (status === 'canceled') {
            config.title = "Cancelar Orçamento";
            config.message = "Deseja cancelar esta proposta pendente?";
            config.confirmText = "Sim, Cancelar";
        }

        setConfirmConfig(config);
    };

    const handleEdit = (e) => { e.stopPropagation(); onEdit(message); };

    return (
        <>
            <div className={`flex flex-col w-full max-w-sm my-4 animate-slide-up ${isMyQuote ? 'ml-auto items-end' : 'mr-auto items-start'}`}>
                <div className="bg-white rounded-[2rem] shadow-xl overflow-hidden border-2 border-emerald-50 w-full">

                    {/* Header da Proposta */}
                    <div className="bg-emerald-600 p-5 text-white flex justify-between items-center relative overflow-hidden">
                        <div className="flex items-center relative z-10">
                            <FileText className="mr-3" size={24} />
                            <div>
                                <h3 className="font-black uppercase tracking-tighter text-sm">Proposta de Orçamento</h3>
                                <p className="text-[10px] opacity-80 font-bold uppercase tracking-widest">
                                    {quoteData.status === 'pending' ? 'Em Análise' : 'Status Atualizado'}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-2 relative z-10">
                            {isMyQuote && quoteData.status === 'pending' && (
                                <>
                                    <button onClick={handleEdit} className="p-2 bg-white/20 hover:bg-white/30 rounded-xl transition-all active:scale-90" title="Editar">
                                        <Pencil size={16} />
                                    </button>
                                    <button onClick={() => requestConfirmation('canceled')} className="p-2 bg-white/20 hover:bg-red-500/40 rounded-xl transition-all active:scale-90" title="Cancelar">
                                        <XCircle size={16} />
                                    </button>
                                </>
                            )}
                            <button onClick={handlePrint} className="p-2 bg-white/20 hover:bg-white/30 rounded-xl transition-all active:scale-90" title="Imprimir">
                                <Printer size={16} />
                            </button>
                        </div>
                        <div className="absolute -right-4 -top-4 w-24 h-24 bg-white/10 rounded-full blur-2xl"></div>
                    </div>

                    <div className="p-6 space-y-4">

                        {/* Status Badge */}
                        <div className={`w-full py-1.5 rounded-full text-center text-[10px] font-black uppercase tracking-widest ${currentStatus.className}`}>
                            {currentStatus.text}
                        </div>

                        {/* Valor Central */}
                        <div className="py-2 text-center border-y border-gray-50">
                            <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-1">Valor Total</p>
                            <p className="text-3xl font-black text-gray-900 tracking-tighter">
                                <span className="text-sm mr-1 text-gray-400 font-bold">R$</span>
                                {quoteData.total ? quoteData.total.toFixed(2).replace('.', ',') : '0,00'}
                            </p>
                        </div>

                        {/* Ver Detalhes */}
                        <button
                            onClick={() => setIsExpanded(!isExpanded)}
                            className="w-full flex items-center justify-between py-2 px-1 group transition-all"
                        >
                            <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest group-hover:text-emerald-600 transition-colors">
                                {isExpanded ? 'Ocultar Itens' : 'Ver Detalhes do Orçamento'}
                            </span>
                            <ChevronDown size={14} className={`text-gray-400 group-hover:text-emerald-600 transition-all duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
                        </button>

                        {isExpanded && quoteData.items && (
                            <div className="space-y-3 pt-1 animate-fade-in">
                                {quoteData.items.map((item, index) => (
                                    <div key={index} className="flex justify-between items-start text-sm">
                                        <span className="text-gray-700 font-bold flex-1 mr-2">{item.description}</span>
                                        <span className="font-black text-gray-900 whitespace-nowrap text-xs">R$ {parseFloat(item.price).toFixed(2)}</span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Rodapé de Ações (Apenas para o Cliente) */}
                        {isClient && quoteData.status === 'pending' && (
                            <div className="mt-4 pt-4 border-t border-gray-50 grid grid-cols-2 gap-3">
                                <button
                                    onClick={() => requestConfirmation('rejected')}
                                    className="py-3.5 bg-gray-100 text-gray-600 font-black text-[10px] uppercase tracking-widest rounded-2xl hover:bg-red-50 hover:text-red-600 transition-all active:scale-95"
                                >
                                    Recusar
                                </button>
                                <button
                                    onClick={() => requestConfirmation('accepted')}
                                    className="py-3.5 bg-emerald-600 text-white font-black text-[10px] uppercase tracking-widest rounded-2xl shadow-lg shadow-emerald-100 hover:bg-emerald-700 transition-all active:scale-95"
                                >
                                    Aceitar
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {confirmConfig && (
                <ConfirmationModal
                    title={confirmConfig.title}
                    message={confirmConfig.message}
                    confirmText={confirmConfig.confirmText}
                    onConfirm={confirmConfig.onConfirm}
                    onClose={() => setConfirmConfig(null)}
                />
            )}
        </>
    );
};

export default QuoteMessage;
